import { useEffect, useMemo, useState } from "react";
import { CreditCard, LogOut, ShieldCheck } from "lucide-react";

import { subscriptionState } from "./subscriptionState.mjs";
export { subscriptionState } from "./subscriptionState.mjs";

function euro(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
}

const styles = `
  .kc-subscription-page {
    --bg: #f5f7fb;
    --surface: #ffffff;
    --surface-2: #f0f3f8;
    --text: #162033;
    --muted: #667085;
    --border: #d9e0ea;
    --primary: #1f4f7a;
    --primary-2: #173a5a;
    --danger: #b42318;
    --gold: #d6ad42;
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 24px;
    box-sizing: border-box;
    background: var(--bg);
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
  .kc-subscription-card {
    width: min(640px, 100%);
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
    display: grid;
    gap: 16px;
    padding: 28px 24px 24px;
    background:
      linear-gradient(180deg, rgba(245,249,253,.92), rgba(255,255,255,1) 170px),
      var(--surface);
    border: 1px solid #c9d8e8;
    border-radius: 14px;
    box-shadow: 0 22px 60px rgba(31,79,122,.10);
  }
  .kc-subscription-card::before {
    content: "";
    height: 5px;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    background: linear-gradient(90deg, var(--primary), var(--gold));
  }
  .kc-eyebrow {
    margin: 0 0 4px;
    color: var(--muted);
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .08em;
  }
  .kc-subscription-card h1 {
    margin: 0;
    color: var(--text);
    font-size: clamp(26px, 4vw, 38px);
    line-height: 1.12;
  }
  .kc-subscription-card p {
    margin: 8px 0 0;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.55;
  }
  .kc-plan-intro {
    margin-top: 2px !important;
  }
  .kc-plan-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0,1fr));
    gap: 12px;
    margin: 2px 0;
  }
  .kc-plan-option {
    min-width: 0;
    position: relative;
    display: grid;
    gap: 6px;
    padding: 16px;
    text-align: left;
    font: inherit;
    color: var(--text);
    background: rgba(255,255,255,.92);
    border: 1px solid var(--border);
    border-radius: 10px;
    cursor: pointer;
    transition: background .14s ease, border-color .14s ease, box-shadow .14s ease;
  }
  .kc-plan-option:hover {
    background: #f8fafc;
    border-color: #b7c6d8;
  }
  .kc-plan-option.selected {
    background: linear-gradient(180deg, #f3f8fd, #fff);
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(31,79,122,.08), 0 14px 30px rgba(31,79,122,.10);
  }
  .kc-plan-label {
    color: var(--muted);
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: .03em;
  }
  .kc-plan-price {
    color: #08213f;
    font-size: 30px;
    line-height: 1.1;
  }
  .kc-plan-copy {
    color: var(--muted);
    font-size: 13px;
    line-height: 1.4;
    padding-right: 4px;
  }
  .kc-plan-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 4px 8px;
    border-radius: 999px;
    background: var(--gold);
    color: #182638;
    font-size: 10px;
    font-weight: 900;
  }
  .kc-billing-section {
    display: grid;
    gap: 14px;
    padding: 16px;
    background: #fff;
    border: 1px solid #d9e4ef;
    border-radius: 10px;
  }
  .kc-billing-section strong {
    color: var(--text);
    font-size: 15px;
  }
  .kc-billing-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0,1fr));
    gap: 12px;
  }
  .kc-field {
    display: grid;
    gap: 6px;
    min-width: 0;
    color: var(--muted);
    font-size: 13px;
    font-weight: 800;
  }
  .kc-field.wide {
    grid-column: 1 / -1;
  }
  .kc-field input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px;
    color: var(--text);
    background: #fff;
    font: inherit;
    font-weight: 400;
    outline: none;
  }
  .kc-field input:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(31,79,122,.08);
  }
  .kc-assurance {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px 16px;
    margin: -4px 0 4px;
    padding: 10px 12px;
    background: #f8fafc;
    border: 1px solid #d9e4ef;
    border-radius: 10px;
    color: #496176;
  }
  .kc-assurance span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 800;
  }
  .kc-assurance span::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #34d399;
  }
  .kc-retention-note {
    margin: 2px 0 4px !important;
    color: #6b7f93 !important;
    font-size: 13px !important;
    line-height: 1.5 !important;
    text-align: center;
  }
  .kc-notice {
    padding: 11px 13px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
  }
  .kc-notice.info {
    border: 1px solid #bed3e9;
    color: #17466f;
    background: #f3f8fd;
  }
  .kc-notice.error {
    border: 1px solid #f3b4ad;
    color: var(--danger);
    background: #fff5f4;
  }
  .kc-button {
    width: 100%;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px 14px;
    cursor: pointer;
    background: var(--surface);
    color: var(--text);
    font: inherit;
    font-size: 13px;
    font-weight: 800;
  }
  .kc-button.primary {
    border-color: var(--primary);
    background: var(--primary);
    color: #fff;
    padding: 12px 14px;
    font-size: 14px;
  }
  .kc-button.primary:hover:not(:disabled) {
    background: var(--primary-2);
  }
  .kc-button:disabled {
    opacity: .55;
    cursor: not-allowed;
  }
  @media (max-width: 640px) {
    .kc-subscription-page { padding: 14px; }
    .kc-subscription-card { padding: 25px 16px 18px; }
    .kc-plan-options,
    .kc-billing-grid { grid-template-columns: 1fr; }
    .kc-field.wide { grid-column: auto; }
    .kc-field input { font-size: 16px; }
  }
`;

export default function SubscriptionGate({ config, tenantId, session, userRole, onLogout, onActivated, previewMode = false }) {
  const state = useMemo(() => subscriptionState(config), [config]);
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(config?.subscription_plan === "yearly" ? "yearly" : "monthly");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState("");
  const initialBilling = config?.billing_details || {};
  const [billing, setBilling] = useState({
    email: initialBilling.email || session?.user?.email || "",
    streetAndNumber: initialBilling.streetAndNumber || "",
    postalCode: initialBilling.postalCode || "",
    city: initialBilling.city || "",
    country: initialBilling.country || "NL",
    organizationNumber: initialBilling.organizationNumber || "",
    vatNumber: initialBilling.vatNumber || "",
  });

  useEffect(() => {
    if (!state.blocked) return;
    fetch("/.netlify/functions/subscription-plans")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => data && setPlans(data))
      .catch(() => {});
  }, [state.blocked]);

  const pending = state.status === "payment_pending";
  const existingSubscription = Boolean(config?.mollie_subscription_id);
  const billingValid = Boolean(
    billing.email.trim() &&
    billing.streetAndNumber.trim() &&
    billing.postalCode.trim() &&
    billing.city.trim() &&
    billing.country.trim() &&
    (billing.organizationNumber.trim() || billing.vatNumber.trim())
  );

  async function authedPost(path, body) {
    const token = session?.access_token;
    const response = await fetch(path, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  }

  async function checkPayment({ quiet = false } = {}) {
    if (checking) return;
    setChecking(true);
    if (!quiet) setMessage("");
    try {
      const { response, data } = await authedPost("/.netlify/functions/check-mollie-payment", { tenant_id: tenantId });
      if (!response.ok) {
        if (!quiet) setMessage(data?.error || "De betaling kon niet worden gecontroleerd.");
      } else if (data.status === "active" || (["expired", "past_due", "cancelled"].includes(data.status) && data.status !== state.status)) {
        if (onActivated) await onActivated();
        else window.location.reload();
        return;
      } else if (!quiet) {
        setMessage(`Mollie-status: ${data.status || "nog niet betaald"}.`);
      }
    } catch {
      if (!quiet) setMessage("De betaling kon niet worden gecontroleerd.");
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    if (!pending) return undefined;
    checkPayment({ quiet: true });
    const timer = window.setInterval(() => checkPayment({ quiet: true }), 3000);
    const timeout = window.setTimeout(() => window.clearInterval(timer), 30000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(timeout);
    };
  }, [pending, tenantId]);

  useEffect(() => {
    const returning = new URLSearchParams(window.location.search).get("payment") === "return";
    if (returning && state.blocked) checkPayment({ quiet: false });
  }, []);

  async function startPayment() {
    if (!billingValid || busy) return;
    setBusy(true);
    setMessage("");
    try {
      const saved = await authedPost("/.netlify/functions/save-billing-details", {
        tenant_id: tenantId,
        billing_details: billing,
      });
      if (!saved.response.ok) {
        setMessage(saved.data?.error || "De factuurgegevens konden niet worden opgeslagen.");
        return;
      }

      const created = await authedPost("/.netlify/functions/create-mollie-payment", {
        tenant_id: tenantId,
        plan: selectedPlan,
      });
      if (!created.response.ok || !created.data?.checkoutUrl) {
        setMessage(created.data?.error || "De betaling kon niet worden gestart.");
        return;
      }
      window.location.href = created.data.checkoutUrl;
    } catch {
      setMessage("De betaling kon niet worden gestart.");
    } finally {
      setBusy(false);
    }
  }

  if (!state.blocked) return null;

  if (userRole !== "owner") {
    return (
      <>
        <style>{styles}</style>
        <main className="kc-subscription-page">
          <section className="kc-subscription-card" style={{ width: "min(520px, 100%)" }}>
            <ShieldCheck size={30} color="#d6ad42" />
            <p className="kc-eyebrow">KitchenControl Keuken</p>
            <h1>Abonnement vereist</h1>
            <p>De proefperiode van <strong>{config?.bedrijfsnaam || "deze KitchenControl-omgeving"}</strong> is afgelopen. Alleen de eigenaar van de omgeving kan het abonnement activeren.</p>
            <button className="kc-button" onClick={onLogout}><LogOut size={16} /> Uitloggen</button>
          </section>
        </main>
      </>
    );
  }

  // A server-reserved checkout fixes the amount for this tenant only.
  // Display the same price that the backend will charge when resuming it.
  const reservedPlan = config?.billing_checkout_id ? config.subscription_plan : null;
  const displayedPlan = (key) => {
    const plan = plans?.[key];
    if (!plan || reservedPlan !== key || !config.subscription_amount) return plan;
    return { ...plan, amount: config.subscription_amount,
      netAmount: (Number(config.subscription_amount) / 1.21).toFixed(2) };
  };
  const monthly = displayedPlan("monthly");
  const yearly = displayedPlan("yearly");

  return (
    <>
      <style>{styles}</style>
      <main className="kc-subscription-page">
        <section className="kc-subscription-card">
          <header>
            <p className="kc-eyebrow">KitchenControl Keuken</p>
            <h1>{existingSubscription ? "Je abonnementsbetaling vraagt aandacht" : pending ? "We ronden je activatie af" : state.status === "trial" ? "Je proefperiode zit erop" : "Je abonnement is niet actief"}</h1>
            <p>
              Je kunt zo weer verder met KitchenControl. Je recepten, HACCP-registraties, mise-en-place gegevens,
              instellingen en overige gegevens blijven bewaard voor <strong>{config?.bedrijfsnaam || "dit bedrijf"}</strong>.
            </p>
          </header>

          {existingSubscription ? (
            <div className="kc-notice info">
              Er bestaat al een abonnement. Controleer je betaling of neem contact op met KitchenControl om de incasso te laten controleren.
              Er wordt geen tweede abonnement aangemaakt.
            </div>
          ) : pending ? (
            <div className="kc-notice info">
              We controleren je betaling automatisch. Meestal is je account binnen enkele seconden actief.
            </div>
          ) : (
            <>
              <p className="kc-plan-intro">
                Kies wat prettig werkt. De betaling loopt via Mollie; daarmee geef je toestemming voor automatische incasso voor het gekozen abonnement.
              </p>

              <div className="kc-plan-options" role="radiogroup" aria-label="Kies abonnement">
                {[
                  ["monthly", "Maandelijks", monthly],
                  ["yearly", "Jaarlijks", yearly],
                ].map(([key, label, plan]) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={selectedPlan === key}
                    disabled={Boolean(reservedPlan && reservedPlan !== key)}
                    className={`kc-plan-option ${selectedPlan === key ? "selected" : ""}`}
                    onClick={() => setSelectedPlan(key)}
                  >
                    {selectedPlan === key && <span className="kc-plan-badge">Gekozen</span>}
                    <span className="kc-plan-label">{label}</span>
                    <strong className="kc-plan-price">{plan ? euro(plan.netAmount) : "…"}</strong>
                    <small className="kc-plan-copy">{plan?.uiSuffix || "Prijs laden…"}</small>
                  </button>
                ))}
              </div>

              {reservedPlan && <p className="kc-notice info">
                Voor deze omgeving staat een {reservedPlan === "monthly" ? "maandabonnement" : "jaarabonnement"} klaar
                van {euro(config.subscription_amount)} inclusief btw per termijn. Dit bedrag wordt ook gebruikt
                voor automatische verlenging totdat je opzegt.
              </p>}

              <section className="kc-billing-section">
                <div>
                  <strong>Factuurgegevens</strong>
                  <p>Deze gegevens gebruikt Mollie voor je factuur. Vul minimaal een KvK-/organisatienummer of btw-nummer in.</p>
                </div>
                <div className="kc-billing-grid">
                  <Field wide label="Factuur-e-mailadres" type="email" value={billing.email} onChange={(v) => setBilling((b) => ({ ...b, email: v }))} />
                  <Field wide label="Straat + huisnummer" value={billing.streetAndNumber} onChange={(v) => setBilling((b) => ({ ...b, streetAndNumber: v }))} />
                  <Field label="Postcode" value={billing.postalCode} onChange={(v) => setBilling((b) => ({ ...b, postalCode: v }))} />
                  <Field label="Plaats" value={billing.city} onChange={(v) => setBilling((b) => ({ ...b, city: v }))} />
                  <Field label="Landcode" value={billing.country} maxLength={2} placeholder="NL" onChange={(v) => setBilling((b) => ({ ...b, country: v.toUpperCase() }))} />
                  <Field label="KvK-/organisatienummer" value={billing.organizationNumber} onChange={(v) => setBilling((b) => ({ ...b, organizationNumber: v }))} />
                  <Field wide label="BTW-nummer" value={billing.vatNumber} placeholder="NL123456789B01" onChange={(v) => setBilling((b) => ({ ...b, vatNumber: v.toUpperCase() }))} />
                </div>
              </section>

              <div className="kc-assurance">
                <span>Beveiligde betaling via Mollie</span>
                <span>Je KitchenControl-omgeving blijft bewaard</span>
              </div>

              <p className="kc-retention-note">
                Ga je niet verder? Geen probleem. Je proefomgeving blijft voorlopig bewaard, zodat je later alsnog kunt activeren.
              </p>
            </>
          )}

          {message && <div className="kc-notice error">{message}</div>}

          {previewMode && (
            <div className="kc-notice info">
              Voorbeeldmodus: deze pagina is alleen zichtbaar gemaakt om de verlopen-proefperiode-UI te beoordelen. Betalen en betaalcontrole zijn hier uitgeschakeld.
            </div>
          )}

          {!existingSubscription && (
            <button
              className="kc-button primary"
              disabled={previewMode || busy || !billingValid || !plans}
              onClick={startPayment}
            >
              <CreditCard size={17} /> {busy ? "Betaling starten…" : pending ? "Ga verder met je betaling" : "Ga door naar Mollie"}
            </button>
          )}

          <button className="kc-button" disabled={previewMode || checking} onClick={() => checkPayment()}>
            {checking ? "Betaling controleren…" : "Controleer betaling"}
          </button>

          <button className="kc-button" onClick={onLogout}>
            <LogOut size={16} /> Uitloggen
          </button>
        </section>
      </main>
    </>
  );
}

function Field({ label, value, onChange, wide, type = "text", maxLength, placeholder }) {
  return (
    <label className={`kc-field ${wide ? "wide" : ""}`}>
      {label}
      <input
        type={type}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
